import React, { useState, useEffect } from "react"
import _ from "lodash"
import uuidV4 from "uuid/v4"

import keyService, * as chord from "data/key"
import automationService from "data/automation"
import { Colors } from "styles/app-styles"

type IAutomation = {
  automationContainerId: string
  automationClass?: string
  automationActive?: boolean
  initialFocusIndex?: number
}

const automationid = uuidV4()

// cell focus
const cellFocusStyle = `outline: 2px solid ${Colors.blackPearl}; outline-offset: -1px;`
const cellNoFocusStyle = ""

const findNestedInput = (node) => {
  if(node.tagName === "INPUT") return node
  else {
    for(let i = 0; i < node.children.length; i++) {
      let childNode = node.children[i]
      if(childNode.tagName === "INPUT") return childNode
      else return findNestedInput(childNode)
    }
  }
}

const findParentDropdownEditable = (ele) => {
  if(ele) {
    if(Array.from(ele.classList).includes("automated")) return ele
    else return findParentDropdownEditable(ele.parentElement)
  } else return null
}

const targetIsChildOfNode = (targetEle: HTMLElement | null, tabableElement: HTMLElement, count: number = 0): boolean => {
  if(!targetEle) return false
  else if(count > 6) return false
  else if(targetEle.isSameNode(tabableElement)) return true
  else return targetIsChildOfNode(targetEle.parentElement, tabableElement, count++)
}

const updateValidation = _.debounce(() => {
  Array.from(document.querySelectorAll('.has-errors')).forEach(el => el.classList.remove('has-errors'))
  Array.from(document.querySelectorAll('[data-error]')).forEach(el => {
    const td = (function getPrimeReactTD(el){
      if (!el || el === document.body) return
      if (el.classList.contains('automated')) return el
      const parent = el.parentElement
      if (parent && parent.nodeName === 'TR') return el
      return getPrimeReactTD(parent!)
    }(el))
    if (td)
      return td.classList.add('has-errors')
    const child = el.children.length == 1 && el.children[0]
    if (el.tagName == 'SPAN' && child && child.tagName == 'DIV')
      return child.classList.add('has-errors')

    el.classList.add('has-errors')
  })
}, 300)

const FormAutomation = (props: IAutomation) => {
  const [automationActive, setAutomationActive] = useState<boolean>(props.automationActive === undefined ? true : props.automationActive)
  const [focusIndex, setFocusIndex] = useState<number>(props.initialFocusIndex || 0)

  const focusCell = (node, shouldSelect?: boolean) => {
    if(node && node.style) node.style.cssText += cellFocusStyle
    if(node && (_.includes(node.className, "p-dropdown") || _.includes(node.className, "p-checkbox"))) {
      const dd = findNestedInput(node)
      if(dd) {
        dd.focus()
        if(_.includes(node.className, "p-checkbox")) node.style.cssText += "border: 0px !important;"
      }
    } else if (node) {
      setTimeout(() => {
        node.focus()
        shouldSelect && node.select && node.select()
      }, 1)
    }
    updateValidation()
  }

  const getEditableNodes = () => {
    let nodes: any[] = []
    if(props.automationClass) nodes = Array.from(document.getElementsByClassName(props.automationClass))
    else nodes = Array.from(document.getElementsByClassName("automated"))
    let editableNodes = _.filter(nodes, node => !_.includes(_.get(node, "className"), "p-disabled"))
    return editableNodes
  }

  const unfocusPrevious = (nodes) => {
    if(nodes[focusIndex] && (_.includes(nodes[focusIndex].className, "p-dropdown") || _.includes(nodes[focusIndex].className, "p-checkbox"))) {
      const dd = findNestedInput(nodes[focusIndex])
      _.invoke(dd, "blur")
    } else {
      //_.invoke(nodes[focusIndex], "blur")
      // this was breaking dropdowns, and doesnt seem to do anything here, must be leftover from table implementation
    }
    if(nodes[focusIndex]) nodes[focusIndex].setAttribute("style", cellNoFocusStyle)
  }

  const unfocusAll = (nodes) => {
    _.forEach(nodes, (n, i) => {
      n.setAttribute("style", cellNoFocusStyle)
    });
  }

  const updateFocus = (nodes, newIndex, shouldSelect?: boolean) => {
    const node = nodes[newIndex]
    unfocusPrevious(nodes)
    focusCell(node, shouldSelect)
    setFocusIndex(newIndex)
  }

  useEffect(() => {
    const editableNodes = getEditableNodes()
    updateFocus(editableNodes, props.initialFocusIndex || 0)
  }, [])

  useEffect(() => {
    const overrideKeyHandlers = e => {
      if(_.includes(["Tab"], e.key)) {
        e.preventDefault()
        return false
      }
    }

    const automationSubscription = automationService.stream().subscribe(event => {
      const editableNodes = getEditableNodes()
      switch(true) {
        case event.type === "REGISTER":
          if(automationid === event.data) {
            setAutomationActive(true)
          } else {
            setAutomationActive(false)
          }
          break
        case event.type === "UNREGISTER":
          if(automationid === event.data) {
            setAutomationActive(false)
          }
          break
      }
    })

    const keySubscription = keyService.stream().subscribe(k => {
      if(!automationActive) return

      const editableNodes = getEditableNodes()
      let newIndex = 0

      switch(true) {
        case k.chord === chord.TAB && k.down === true:
          newIndex = focusIndex + 1
          if(newIndex < editableNodes.length) {
            updateFocus(editableNodes, newIndex, true)
          } else {
            updateFocus(editableNodes, 0, true)
          }
          break
        case k.chord === chord.SHIFT_TAB && k.down === true:
          newIndex = focusIndex - 1
          if(newIndex >= 0) {
            updateFocus(editableNodes, newIndex, true)
          } else {
            updateFocus(editableNodes, editableNodes.length - 1, true)
          }
          break
      }
    })

    const clickSync = e => {
      if(!e.isTrusted) return

      const automationContainer = document.getElementById(props.automationContainerId || "")
      const editableNodes = getEditableNodes()

      let target_rect = e.target.getBoundingClientRect()
      if(e.target && e.target.className && _.isString(e.target.className) && (e.target.className.includes("p-dropdown") || e.target.className.includes('p-checkbox'))){
        const parentDD = findParentDropdownEditable(e.target)
        if(parentDD) target_rect = parentDD.getBoundingClientRect()
      }

      let n_idx = -1
      _.forEach(editableNodes, (n, i) => {
        let n_rect = n.getBoundingClientRect()
        if(-2 <= n_rect.x - target_rect.x &&
            n_rect.x - target_rect.x <= 2 &&
            -2 <= n_rect.y - target_rect.y &&
            n_rect.y - target_rect.y <= 2 &&
            -2 <= n_rect.width - target_rect.width &&
            n_rect.width - target_rect.width <= 2 &&
            -2 <= n_rect.height - target_rect.height &&
            n_rect.height - target_rect.height <= 2)
        {
          n_idx = i
          return false
        } else if(-2 <= n_rect.x - target_rect.x &&
          n_rect.x - target_rect.x <= 2 &&
          -2 <= n_rect.y - target_rect.y &&
          n_rect.y - target_rect.y <= 2 &&
          target_rect.width === 0 &&
          target_rect.height === 0)
        {
          n_idx = i
          return false
        } else if(targetIsChildOfNode(e.target, n)) {
          n_idx = i
          return false
        }
      })

      if(n_idx >= 0) {
        automationService.broadcast("REGISTER", automationid)
        unfocusAll(editableNodes)
        let node = editableNodes[n_idx]
        if(node && node.style) node.style.cssText += cellFocusStyle
        setFocusIndex(n_idx)
      } else {
        // if user clicks inside automation container, but not on a control, focus on the first selectable control
        if(automationContainer && automationContainer.contains(e.target)) {
          // automationService.broadcast("REGISTER", automationid)
          // setTimeout(() => updateFocus(editableNodes, 0), 0)
        }
        // otherwise, if user clicks outside automation container, give up control of automation
        automationService.broadcast("UNREGISTER", automationid)
        unfocusPrevious(editableNodes)
      }
    }

    document.addEventListener("keydown", overrideKeyHandlers)
    document.addEventListener("select", clickSync)
    document.addEventListener("click", clickSync)

    updateValidation()
    return function cleanup() {
      automationSubscription.unsubscribe()
      keySubscription.unsubscribe()
      document.removeEventListener("keydown", overrideKeyHandlers)
      document.removeEventListener("select", clickSync)
      document.removeEventListener("click", clickSync)
    }
  }, [automationActive, focusIndex])

  return null
}

export default FormAutomation
